import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import {
  useDeleteGlobalProhibitedKeyword,
  useGetGlobalProhibitedKeywords,
} from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";
import {
  useDeleteProhibitedKeyword,
  useGetProhibitedKeywords,
} from "@/resources/prohibited-keyword/prohibited-keyword.api";
import {
  useDeleteProductProhibitedKeyword,
  useGetProductProhibitedKeywords,
} from "@/resources/product-prohibited-keyword/product-prohibited-keyword.api";
import { toast } from "@/hooks/use-toast";
import { AddProhibitedKeywordDialog } from "./AddProhibitedKeywordDialog";

type Tier = "global" | "brand" | "product";

interface ProhibitedKeywordsOverviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: number | null;
  productId: number;
  productName: string;
}

// AUT-41: cross-tier view (Product + Brand + Global side by side), matching the
// prototype's "Prohibited Keywords" menu item -- shared between the product
// detail page's kebab menu and the Search Terms per-product button, so both
// entry points show what's blocking the product at every tier, not just its
// own. Adds remove capability the prototype's equivalent didn't have.
export function ProhibitedKeywordsOverviewModal({
  open,
  onOpenChange,
  brandId,
  productId,
  productName,
}: ProhibitedKeywordsOverviewModalProps) {
  const { data: globalKeywords = [] } = useGetGlobalProhibitedKeywords();
  const { data: brandKeywords = [] } = useGetProhibitedKeywords(brandId);
  const { data: productKeywords = [] } = useGetProductProhibitedKeywords(productId);

  const deleteGlobal = useDeleteGlobalProhibitedKeyword();
  const deleteBrand = useDeleteProhibitedKeyword();
  const deleteProduct = useDeleteProductProhibitedKeyword();

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<{ tier: Tier; id: number; keyword: string } | null>(null);

  // AUT-220: the confirm dialog closes as soon as the request is fired instead of
  // waiting for it to settle. The delete is already optimistic, and the server work
  // behind it (rebuilding search terms for the brand -- platform-wide for the global
  // tier) can run for minutes, which used to hold the dialog open and keep every
  // remove button disabled: "I'm not able to delete any other keywords until I
  // refresh the page".
  const handleConfirmRemove = () => {
    if (!toRemove) return;
    const { tier, id, keyword } = toRemove;
    setToRemove(null);

    const tierLabels: Record<Tier, string> = { global: "Global", brand: "Brand", product: "Product" };
    const onSuccess = () =>
      toast({
        title: "Removed",
        description: `"${keyword}" removed from ${tierLabels[tier]} prohibited keywords.`,
      });

    if (tier === "global") deleteGlobal.mutate({ id }, { onSuccess });
    else if (tier === "brand" && brandId) deleteBrand.mutate({ id, brandId }, { onSuccess });
    else if (tier === "product") deleteProduct.mutate({ id, productId }, { onSuccess });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pr-8">
            <div className="space-y-1">
              <DialogTitle>Prohibited Keywords</DialogTitle>
              <DialogDescription>
                Keywords listed here will never appear in this product's generated search terms.
              </DialogDescription>
            </div>
            <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              Add a keyword
            </Button>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <KeywordColumn
              label="Product-specific"
              items={productKeywords}
              emptyText="No prohibited keywords yet for this product."
              onRemove={(item) => setToRemove({ tier: "product", ...item })}
              onAdd={() => setAddOpen(true)}
            />
            <KeywordColumn
              label="Brand-specific"
              items={brandKeywords}
              emptyText="No prohibited keywords yet for this brand."
              onRemove={(item) => setToRemove({ tier: "brand", ...item })}
              onAdd={() => setAddOpen(true)}
            />
            <KeywordColumn
              label="Global"
              items={globalKeywords}
              emptyText="No global keywords yet."
              onRemove={(item) => setToRemove({ tier: "global", ...item })}
              onAdd={() => setAddOpen(true)}
            />
          </div>
        </DialogContent>
      </Dialog>

      <AddProhibitedKeywordDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        keyword=""
        brandId={brandId}
        productId={productId}
        productName={productName}
      />

      <ConfirmDeleteDialog
        open={!!toRemove}
        onOpenChange={(o) => {
          if (!o) setToRemove(null);
        }}
        title={`Remove "${toRemove?.keyword}" from prohibited keywords?`}
        description="This keyword will no longer be blocked at this tier."
        confirmLabel="Remove"
        onConfirm={handleConfirmRemove}
      />
    </>
  );
}

interface KeywordColumnProps {
  label: string;
  items: Array<{ id: number; keyword: string }>;
  emptyText: string;
  onRemove: (item: { id: number; keyword: string }) => void;
  onAdd?: () => void;
}

function KeywordColumn({ label, items, emptyText, onRemove, onAdd }: KeywordColumnProps) {
  const sorted = useMemo(() => [...items].sort((a, b) => a.keyword.localeCompare(b.keyword)), [items]);

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</h4>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </div>

      {sorted.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6 text-center rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground px-2">{emptyText}</p>
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="text-sm font-medium text-primary hover:text-primary/80"
            >
              Add a keyword
            </button>
          )}
        </div>
      ) : (
        <ScrollArea className="h-[50vh]">
          <div className="flex flex-wrap gap-1.5 pr-2">
            {sorted.map((item) => (
              <Badge
                key={item.id}
                className="text-sm gap-1 pr-1 bg-status-error-soft text-foreground border-destructive/30 hover:bg-destructive/20"
              >
                {item.keyword}
                <button
                  type="button"
                  onClick={() => onRemove(item)}
                  aria-label={`Remove ${item.keyword}`}
                  className="hover:opacity-70"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
